import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';
import { requisitionPositionText, seatProgressText } from '../requisitionActions';
import { CONTRACT_TYPES, REMOTE_TYPES, useRequisitionForm } from './requisitionForm';
import BandDeviationBadge from '../BandDeviationBadge';

export const REQUISITION_STATUSES = [
  'draft',
  'pending_approval',
  'approved',
  // İlan açılınca backend talebi buraya alır (spec donar); oradan yalnız fill | cancel.
  'posting_opened',
  'rejected',
  'on_hold',
  'filled',
  'cancelled',
];

// BE enum values — see Models/AtsRequisition.cs (sabitler form modülünde; buradan yeniden dışa açılır).
export { CONTRACT_TYPES, EMPLOYMENT_TYPES, REMOTE_TYPES } from './requisitionForm';

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

// `writable` = AT.Write / admin. BP-13 (Parti 9): onaycı (İK İş Ortağı) atamak artık
// yalnız İK'nın işi — backend yetkisiz çağıranda 403
// `ats.requisition.hrBpAssignmentNotAllowed` döner. Alan formda kalsaydı yönetici
// dolduracak, kaydedecek ve hata alacaktı; arayüz backend'in kabul etmeyeceği alanı
// hiç göstermez.
const useAtsRequisitionsApi = (queryParams, { writable = false } = {}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/ats-requisitions?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  // Gövde üreticisi ve form şeması `requisitionForm.js`'te (Ekibimin Şeması da kullanır).
  const { objectSchema, buildPayload, noteHiringManager, buildPrefill } = useRequisitionForm({ writable });

  const create = useCallback(async (formData) => {
    try {
      await api.post('/v1/ats-requisitions', buildPayload(formData));
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh, buildPayload]);

  // Backend `{ returnedToDraft }` döndürür (onay bekleyen talep düzenlenince taslağa döner);
  // sayfa bunu bildirir. Hata yolunda false (toast interceptor'da).
  const update = useCallback(async (id, formData) => {
    try {
      const res = await api.put(`/v1/ats-requisitions/${id}`, buildPayload(formData, { isUpdate: true }));
      refresh();
      return res.data?.data ?? true;
    } catch {
      return false;
    }
  }, [refresh, buildPayload]);

  // `extra`: geçişe özgü ek alanlar — `closeUnfilledPositions` (iptal / erken kapanış,
  // yalnız `new` modda), `approvedHeadcount` (onay, yalnız `new` modda).
  const changeStatus = useCallback(async (id, action, reason, extra) => {
    try {
      await api.post(`/v1/ats-requisitions/${id}/status`, { action, reason, ...(extra || {}) });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const remove = useCallback(async (id) => {
    try {
      await api.delete(`/v1/ats-requisitions/${id}`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const columns = useMemo(() => {
    const statusLabel = (s) => t(`ats.requisition.status.${s}`, { defaultValue: s });
    const contractLabel = (c) => t(`ats.requisition.contractType.${c}`, { defaultValue: c });
    const remoteLabel = (r) => t(`ats.requisition.remoteType.${r}`, { defaultValue: r });

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      // Position display: "[CODE] Title Name" — same widget the form uses, so
      // recruiters see exactly what they picked. Backed by position[like] which
      // OR-matches Position.Code or Position.Title.Name.
      {
        name: 'positionDisplay',
        label: t('ats.requisition.positionId'),
        options: {
          filter: true,
          sort: false,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'position[like]' },
          customBodyRender: (_, tableMeta) => requisitionPositionText(data[tableMeta.rowIndex]),
        },
      },
      {
        name: 'departmentName',
        label: t('ats.requisition.departmentId'),
        options: {
          filter: false, sort: false,
          customBodyRender: (v) => v || '-',
        },
      },
      // FE-17 — onaycının "nerede" sorusu. Backend `location[like]` süzgeci
      // AtsRequisitionController'da açıktır; kolon adı `location` olduğu için
      // parseTableParams anahtarı kendiliğinden `location[like]` üretir
      // (filterKey YAZILMAZ — gereksiz, ve yanlış yazılırsa sessizce ölür).
      {
        name: 'location',
        label: t('ats.requisition.location'),
        options: {
          filter: true, sort: false,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like' },
          customBodyRender: (v) => v || '-',
        },
      },
      // FE-17 — kimin istediği ve kimin onaylayacağı. Adlar backend'de
      // (AtsRequisitionDataAccess.ResolvePersonNamesAsync) tek toplu sorguda
      // çözülür; burada süzgeç/sıralama YOK çünkü backend'de karşılığı yok
      // (olmayan süzgeç eklemek sessiz no-op üretir).
      {
        name: 'hiringManagerName',
        label: t('ats.requisition.hiringManagerUserId'),
        options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
      },
      {
        name: 'hrBpName',
        label: t('ats.requisition.hrBpUserId'),
        options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
      },
      // Sıralama: backend AtsRequisitionDataAccess orderBy switch'i status | approvedat
      // (varsayılan dal CreateDate) — `sortKey` bunlara eşler; diğer kolonlar `sort: false`.
      {
        name: 'status',
        label: t('ats.requisition.statusLabel'),
        options: {
          ...multiSelectFilter(t('ats.requisition.statusLabel'), REQUISITION_STATUSES, statusLabel),
          sortKey: 'status',
          customBodyRender: (v) => statusLabel(v),
        },
      },
      numericFilterColumn('requestedHeadcount', t('ats.requisition.requestedHeadcount'), { decimals: 0, sort: false }),
      // Koltuk ilerlemesi "3/20 Dolu" — talebin pozisyonlarından kaçına aktif atama
      // var. Legacy (ve henüz pozisyon açmamış `new`) satırda eski kişi sayısı.
      {
        name: 'seatsFilled',
        label: t('ats.requisition.seatsColumn'),
        options: {
          filter: false, sort: false,
          customBodyRender: (_, tableMeta) => seatProgressText(data[tableMeta.rowIndex], t),
        },
      },
      {
        name: 'contractType',
        label: t('ats.requisition.contractType.label'),
        options: {
          ...multiSelectFilter(t('ats.requisition.contractType.label'), CONTRACT_TYPES, contractLabel),
          sort: false,
          customBodyRender: (v) => v ? contractLabel(v) : '-',
        },
      },
      {
        name: 'remoteType',
        label: t('ats.requisition.remoteType.label'),
        options: {
          ...multiSelectFilter(t('ats.requisition.remoteType.label'), REMOTE_TYPES, remoteLabel),
          sort: false,
          customBodyRender: (v) => v ? remoteLabel(v) : '-',
        },
      },
      numericFilterColumn('minSalary', t('ats.requisition.minSalary'), { decimals: 2, sort: false }),
      numericFilterColumn('maxSalary', t('ats.requisition.maxSalary'), { decimals: 2, sort: false }),
      // Kademe bandı sapması — gönderimde sunucu hesaplar; onaycı satır düğmelerinin yanında görür.
      {
        name: 'salaryBandDeviation',
        label: t('ats.requisition.bandDeviation.label'),
        options: {
          filter: false, sort: false,
          customBodyRender: (v) => (v ? <BandDeviationBadge deviation={v} /> : '-'),
        },
      },
      // createdate switch'te ayrı dal değil: bilinmeyen anahtar CreateDate'e (varsayılan) düşer, yön korunur.
      dateFilterColumn('createDate', t('ats.requisition.createDate'), lang, { sortKey: 'createdate' }),
      dateFilterColumn('approvedAt', t('ats.requisition.approvedAt'), lang, { sortKey: 'approvedat' }),
    ];
  }, [t, lang, data]);


  return {
    fetching, data, pagination, columns, objectSchema, refresh, create, update, changeStatus, remove,
    noteHiringManager, buildPrefill,
  };
};

export default useAtsRequisitionsApi;
